import { supabase } from './supabase';

export interface VisitEvent {
  id: string;
  occurred_at: string;
  country: string | null;
  device: string | null;
  utm_source: string | null;
  referrer: string | null;
  user_agent?: string | null;
  user_id?: string | null;
  display_name?: string | null;
  is_admin?: boolean;
}

export interface DailyVisitCount {
  visit_date: string;
  total_visits: number;
}

export interface VisitAnalytics {
  totalVisits: number;
  dailyCounts: DailyVisitCount[];
  recentVisits: VisitEvent[];
}

const VISIT_TABLE = 'visit_events';
const DAILY_VIEW = 'visit_events_daily';

/**
 * Load total visits, daily counts, and recent visit rows for the admin analytics view.
 * Uses the visit_events_daily view for grouping to avoid client-side aggregation.
 */
export async function fetchVisitAnalytics(
  recentLimit = 200
): Promise<VisitAnalytics> {
  const [
    { count, error: countError },
    { data: dailyData, error: dailyError },
    { data: recentData, error: recentError },
  ] = await Promise.all([
    supabase.from(VISIT_TABLE).select('id', { count: 'exact', head: true }),
    supabase
      .from(DAILY_VIEW)
      .select('visit_date, total_visits')
      .order('visit_date', { ascending: true }),
    supabase
      .from(VISIT_TABLE)
      .select(
        'id, occurred_at, country, device, utm_source, referrer, user_agent, user_id'
      )
      .order('occurred_at', { ascending: false })
      .limit(recentLimit),
  ]);

  if (countError) throw countError;
  if (dailyError) throw dailyError;
  if (recentError) throw recentError;

  const recentRows = (recentData as VisitEvent[]) ?? [];

  // If any recent rows include a user_id, fetch display names from user_settings.
  const userIds = Array.from(
    new Set(recentRows.map((r) => r.user_id).filter(Boolean) as string[])
  );
  if (userIds.length > 0) {
    const [settingsResult, rolesResult] = await Promise.all([
      supabase
        .from('user_settings')
        .select('user_id, display_name')
        .in('user_id', userIds as string[]),
      supabase
        .from('user_roles')
        .select('user_id, role')
        .in('user_id', userIds as string[]),
    ]);

    if (!settingsResult.error && settingsResult.data) {
      const map = new Map<string, string | null>();
      settingsResult.data.forEach((s: any) =>
        map.set(s.user_id, s.display_name ?? null)
      );
      recentRows.forEach((r) => {
        if (r.user_id) r.display_name = map.get(r.user_id) ?? null;
      });
    }

    if (!rolesResult.error && rolesResult.data) {
      const adminSet = new Set(
        rolesResult.data
          .filter(
            (entry: any) =>
              typeof entry?.role === 'string' &&
              entry.role.toLowerCase() === 'admin'
          )
          .map((entry: any) => entry.user_id)
      );
      recentRows.forEach((r) => {
        if (r.user_id) {
          r.is_admin = adminSet.has(r.user_id);
        }
      });
    }
  }

  return {
    totalVisits: count ?? 0,
    dailyCounts: (dailyData as DailyVisitCount[]) ?? [],
    recentVisits: recentRows,
  };
}

export async function deleteVisitEvent(id: string): Promise<void> {
  const { error } = await supabase.from(VISIT_TABLE).delete().eq('id', id);
  if (error) throw error;
}

export async function deleteVisitEvents(ids: string[]): Promise<void> {
  if (!Array.isArray(ids) || ids.length === 0) return;
  const { error } = await supabase.from(VISIT_TABLE).delete().in('id', ids);
  if (error) throw error;
}
